import { useTranslations } from '@vocab/react';
import { Box, Loader } from 'braid-design-system';
import {
  useRef,
  type Ref,
  forwardRef,
  memo,
  useImperativeHandle,
  useEffect,
} from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';

import { SECTION_UPSELL } from 'src/app/shared/constants/pageSectionName';
import { JOB_AD_EDIT_PRODUCT_ID } from 'src/app/shared/constants/productIds';
import { SHOPPING_CART_KEY_EDIT_FLOW } from 'src/app/shared/constants/shoppingCart';
import { useShoppingCart } from 'src/app/shared/hooks/useShoppingCart';
import { getAdProductFromCart } from 'src/app/utils/getAdProductFromCart';
import { log } from 'src/app/utils/logging';
import {
  actionResetProductDisplayName,
  actionUpdateProductDisplayName,
} from 'src/app/v1/state/modules/formData/productDisplayName';
import { actionUpdateBuyUpSell } from 'src/app/v1/state/modules/session/buyUpSell';
import { actionUpdateEditFlowCheckout } from 'src/app/v1/state/modules/session/editFlowCheckout';
import { AdSelectionWrapperEditFlow } from 'src/app/v1/views/components/AdSelectionWrapper/AdSelectionWrapperEditFlow';
import { EditablePageSection } from 'src/app/v1/views/sections/EditablePageSection';
import type { AdProductUpgrade, ProductList } from 'src/graphql/generated';
import type { AppState } from 'src/types/appState';
import type { KeySellingPointsRef } from 'src/types/formFields';
import type { SectionRef } from 'src/types/sections';

import translations from './.vocab';
import { Edit } from './Edit';

export const UpsellSection = memo(
  forwardRef((_, ref: Ref<SectionRef>) => {
    const { buyUpSell, productDisplayName } = useSelector(
      (state: AppState) => ({
        buyUpSell: state.session.buyUpSell,
        productDisplayName: state.formData.productDisplayName,
      }),
      shallowEqual,
    );

    const { t } = useTranslations(translations);

    const editablePageSectionRef = useRef<SectionRef>(null);
    const keySellingPointsRef = useRef<KeySellingPointsRef>(null);

    const dispatch = useDispatch();

    const { cartState, productResponses } = useShoppingCart();
    const { loading, products } =
      productResponses?.[SHOPPING_CART_KEY_EDIT_FLOW] || {};

    // On editFlow page load, the JobAdEdit product should be added to the cart items already
    const hasUpgradedProductInCart = (cartState?.items?.length ?? 0) > 1;

    useImperativeHandle(
      ref,
      () =>
        ({
          ...editablePageSectionRef.current,
          edit: buyUpSell,
        } as SectionRef),
    );

    useEffect(() => {
      const updateSelectedProduct = () => {
        if (products as ProductList) {
          const upgradeProducts = getAdProductFromCart(
            cartState?.items || [],
            products.items || [],
            'AdProductUpgrade',
          );

          const selectedUpgradeProduct = upgradeProducts.filter(
            (product) => product.id !== JOB_AD_EDIT_PRODUCT_ID,
          )[0] as AdProductUpgrade;

          if (
            !selectedUpgradeProduct ||
            !selectedUpgradeProduct.displayMetadata?.name
          ) {
            log.error(
              'UpsellSection | no product display name found for selected product',
              { selectedUpgradeProduct },
            );
            return;
          }

          dispatch(
            actionUpdateProductDisplayName(
              selectedUpgradeProduct.displayMetadata.name,
            ),
          );
        }
      };

      if (hasUpgradedProductInCart && !loading) {
        dispatch(actionUpdateBuyUpSell(true));
        dispatch(actionUpdateEditFlowCheckout({ enabled: true }));
        updateSelectedProduct();
      } else {
        dispatch(actionUpdateBuyUpSell(false));
        dispatch(actionUpdateEditFlowCheckout({ enabled: false }));
        dispatch(actionResetProductDisplayName());
      }
    }, [
      cartState?.items,
      dispatch,
      hasUpgradedProductInCart,
      loading,
      products,
    ]);

    return (
      <>
        <AdSelectionWrapperEditFlow />
        {hasUpgradedProductInCart && (
          <>
            {loading === undefined || loading ? (
              <Loader />
            ) : (
              <Box marginTop="large">
                <EditablePageSection
                  title={t('upsell-section-title', {
                    productDisplayName: productDisplayName ?? '',
                  })}
                  name={SECTION_UPSELL}
                  ref={editablePageSectionRef}
                  fields={[keySellingPointsRef]}
                  read={null}
                  edit={<Edit refs={{ keySellingPointsRef }} />}
                  defaultIsEdit
                />
              </Box>
            )}
          </>
        )}
      </>
    );
  }),
);
